import { FC, useMemo } from 'react';

import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';
import { NPICurrencyInputFieldLabelled } from 'src/components/FormikComponents/NPICurrencyInputField/NPICurrencyInputFieldLabelled';

import RateInputField from '../RateInputField';

enum RateType {
    PERCENTAGE = 'PERCENTAGE',
    PER_ITEM = 'PER_ITEM',
    BOTH = 'BOTH',
}

interface ThreeToggleRates {
    isOdd?: boolean;
    namePrefix: string;
    heading: string;
    isConstant?: boolean;
}

export const TRIPLE_TOGGLE_PRICING_TYPES = [
    { label: 'Percentage', value: RateType.PERCENTAGE },
    { label: 'Per Item', value: RateType.PER_ITEM },
    { label: 'Both', value: RateType.BOTH },
];

interface LowestHighest {
    heading: string;
    name: string;
    disabled?: boolean;
    type?: RateType.PERCENTAGE | RateType.PER_ITEM;
}

const LowestHighest: FC<LowestHighest> = ({ heading, name, disabled, type }) => {
    return (
        <Flex flexDirection="column" gap="8px" width="100%">
            <Typography fontSize="16px" fontWeight="600" lineHeight="24px" color="#575757">
                {heading}
            </Typography>
            <Box height="1px" width="100%" bgcolor="#F18F34" />
            <Flex gap="8px">
                {type === RateType.PERCENTAGE ? (
                    <>
                        <RateInputField
                            name={`${name}LowestRate`}
                            label="Lowest Rate"
                            disabled={disabled}
                        />
                        <RateInputField
                            name={`${name}HighestRate`}
                            label="Highest Rate"
                            disabled={disabled}
                        />
                    </>
                ) : (
                    <>
                        <NPICurrencyInputFieldLabelled
                            fieldName={`${name}LowestRate`}
                            header="Lowest Rate"
                            isDisabled={disabled}
                        />
                        <NPICurrencyInputFieldLabelled
                            fieldName={`${name}HighestRate`}
                            header="Highest Rate"
                            isDisabled={disabled}
                        />
                    </>
                )}
            </Flex>
        </Flex>
    );
};

export const ThreeToggleRates: FC<ThreeToggleRates> = ({
    isOdd,
    namePrefix,
    heading,
    isConstant,
}) => {
    const [{ value }, _, { setValue }] = useField(`${namePrefix}.type`);

    const isTypeBoth = value === RateType.BOTH;
    const isPercentage = value === RateType.PERCENTAGE;
    const isPerItem = value === RateType.PER_ITEM;

    const name = useMemo(() => {
        if (isPercentage) {
            return `${namePrefix}.percentage`;
        } else if (isPerItem) {
            return `${namePrefix}.perItem`;
        }
        return namePrefix;
    }, [namePrefix, isPercentage, isPerItem]);

    return (
        <Flex
            padding="16px 8px"
            bgcolor={isOdd ? 'rgba(250, 218, 187, 0.15)' : 'transparent'}
            flexDirection={isTypeBoth ? 'row' : 'column'}
            gap="24px"
            justifyContent={isTypeBoth ? 'space-between' : 'flex-start'}
        >
            <Flex flexDirection="column" flex={1}>
                <Flex
                    flexDirection={isTypeBoth ? 'column' : 'row'}
                    justifyContent="space-between"
                    flex={isTypeBoth ? undefined : 1}
                >
                    <Typography fontSize="20px" fontWeight={500} lineHeight="30px" color="#686867">
                        {heading}
                    </Typography>
                    <RadioGroup
                        row
                        onChange={(e) => {
                            setValue(e.target.value);
                        }}
                    >
                        {TRIPLE_TOGGLE_PRICING_TYPES.map(({ label, value: optionValue }) => {
                            return (
                                <FormControlLabel
                                    key={optionValue}
                                    value={optionValue}
                                    control={<Radio />}
                                    checked={value === optionValue}
                                    label={label}
                                    disabled={isConstant}
                                />
                            );
                        })}
                    </RadioGroup>
                </Flex>
                {isTypeBoth ? (
                    <Flex flexDirection="column" gap="16px" maxWidth="80%">
                        <RateInputField
                            name={`${namePrefix}.baseRatePercentage`}
                            label="Base Rate (%)"
                            disabled={isConstant}
                        />
                        <NPICurrencyInputFieldLabelled
                            fieldName={`${namePrefix}.perItemBaseRate`}
                            header="Per Item Base Rate"
                            isDisabled={isConstant}
                        />
                    </Flex>
                ) : (
                    <Flex width="100%" justifyContent="space-between">
                        {isPercentage ? (
                            <RateInputField
                                name={`${name}BaseRate`}
                                label="Base Rate"
                                disabled={isConstant}
                            />
                        ) : (
                            <NPICurrencyInputFieldLabelled
                                fieldName={`${name}BaseRate`}
                                header="Base Rate"
                                isDisabled={isConstant}
                            />
                        )}

                        {isPercentage ? (
                            <RateInputField
                                name={`${name}HighestRate`}
                                label="Highest Rate"
                                disabled={isConstant}
                            />
                        ) : (
                            <NPICurrencyInputFieldLabelled
                                fieldName={`${name}HighestRate`}
                                header="Highest Rate"
                                isDisabled={isConstant}
                            />
                        )}

                        {isPercentage ? (
                            <RateInputField
                                name={`${name}LowestRate`}
                                label="Lowest Rate"
                                disabled={isConstant}
                            />
                        ) : (
                            <NPICurrencyInputFieldLabelled
                                fieldName={`${name}LowestRate`}
                                header="Lowest Rate"
                                isDisabled={isConstant}
                            />
                        )}
                    </Flex>
                )}
            </Flex>
            {isTypeBoth && (
                <Flex flexDirection="column" flex={1}>
                    <LowestHighest
                        heading="Percentage"
                        name={`${namePrefix}.percentage`}
                        disabled={isConstant}
                        type={RateType.PERCENTAGE}
                    />
                    <LowestHighest
                        heading="Per Item"
                        name={`${namePrefix}.perItem`}
                        disabled={isConstant}
                        type={RateType.PER_ITEM}
                    />
                </Flex>
            )}
        </Flex>
    );
};
